/**
 * Cuándo le toca el siguiente monitoreo a un riesgo o a un activo
 * (29/09/2026, avisos de monitoreo). Es la ÚNICA regla: la usan el tablero
 * (el aviso «Te toca monitorear» y los contadores de cada matriz) y la
 * matriz (el aviso en cada riesgo y en cada activo). Los avisos son solo en
 * pantalla (decisión de Norman): el servidor no calcula nada, solo manda las
 * fechas (MatrizDao.monitoreos).
 *
 *   - Próximo = último corte + su periodicidad; sin cortes, desde la fecha
 *     de identificación.
 *   - Riesgos: la periodicidad del FOS-005, que es texto («Mensual»,
 *     «Quincenal», «Cuatrimestral (cada 4 meses)»...).
 *   - Activos: cada 4 meses, la cadencia acordada (IaDashboardBuss usa 120
 *     días para lo mismo).
 *   - Por vencer: desde 5 días antes. Vencido: pasada la fecha, y así se
 *     queda hasta que alguien registre el corte.
 *   - Alerta flotante (15/10/2026): desde 3 días antes y mientras siga
 *     vencido, en cualquier pantalla, a quien puede registrar el corte.
 */

/** Desde cuántos días antes se avisa. */
export const DIAS_AVISO = 5;

/** Cada cuánto se monitorean los activos. */
export const MESES_ACTIVOS = 4;

/** Desde cuántos días antes sale la alerta flotante (15/10/2026). */
export const DIAS_ALERTA = 3;

export type EstadoMonitoreo = "VENCIDO" | "POR_VENCER" | "AL_DIA" | "SIN_DATOS";

export interface ProximoMonitoreo {
  estado: EstadoMonitoreo;
  /** Cuándo toca; null si no se puede saber. */
  fecha: Date | null;
  /** Días que faltan (negativos: días de retraso); null si no se sabe. */
  dias: number | null;
  /** Corto, para una etiqueta: «Vencido hace 3 días», «Vence en 2 días»... */
  texto: string;
  /** Completo, con la fecha: «Tocaba el 13/09/2026 · vencido hace 16 días». */
  detalle: string;
}

interface Intervalo {
  dias?: number;
  meses?: number;
}

type Fecha = Date | string | number | null | undefined;

/** Si hay que avisarlo: vencido o por vencer. */
export function pideAviso(p: ProximoMonitoreo): boolean {
  return p.estado === "VENCIDO" || p.estado === "POR_VENCER";
}

/** Si toca la alerta flotante: vence en 3 días o menos, o ya venció. */
export function pideAlerta(p: ProximoMonitoreo): boolean {
  return p.dias !== null && p.dias <= DIAS_ALERTA;
}

/** La periodicidad del FOS-005 en días o meses; null si no se entiende. */
export function intervaloDe(periodicidad?: string | null): Intervalo | null {
  const t = sinAcentos(periodicidad);
  if (!t) {
    return null;
  }
  // «cada 4 meses», «cada 15 dias», «cada 2 semanas»
  const cada = t.match(/cada\s+(\d+)\s*(dia|semana|mes)/);
  if (cada) {
    const n = Number.parseInt(cada[1], 10);
    if (n > 0) {
      if (cada[2] === "mes") {
        return { meses: n };
      }
      return { dias: cada[2] === "semana" ? n * 7 : n };
    }
  }
  // «Cuatrimestral» antes que «trimestral»: lo contiene.
  if (t.includes("cuatrimes")) {
    return { meses: 4 };
  }
  if (t.includes("trimes")) {
    return { meses: 3 };
  }
  if (t.includes("bimes")) {
    return { meses: 2 };
  }
  if (t.includes("semes")) {
    return { meses: 6 };
  }
  if (t.includes("anual")) {
    return { meses: 12 };
  }
  if (t.includes("mensual")) {
    return { meses: 1 };
  }
  if (t.includes("quincen")) {
    return { dias: 15 };
  }
  if (t.includes("seman")) {
    return { dias: 7 };
  }
  if (t.includes("diari")) {
    return { dias: 1 };
  }
  return null;
}

/**
 * El próximo monitoreo. Los riesgos van con su periodicidad; los activos
 * (esActivo) cada 4 meses, sin importar la periodicidad que traigan.
 */
export function proximoMonitoreo(
  periodicidad: string | null | undefined,
  ultimoCorte: Fecha,
  fechaIdentificacion: Fecha,
  esActivo = false,
  hoy: Date = new Date(),
): ProximoMonitoreo {
  const intervalo = esActivo ? { meses: MESES_ACTIVOS } : intervaloDe(periodicidad);
  if (!intervalo) {
    const texto = periodicidad
      ? "No se reconoce la periodicidad «" + periodicidad + "»"
      : "Sin periodicidad de monitoreo";
    return { estado: "SIN_DATOS", fecha: null, dias: null, texto, detalle: texto };
  }
  const base = aDia(ultimoCorte) || aDia(fechaIdentificacion);
  if (!base) {
    const texto = "Sin cortes ni fecha de identificación";
    return { estado: "SIN_DATOS", fecha: null, dias: null, texto, detalle: texto };
  }
  const fecha = intervalo.meses ? sumaMeses(base, intervalo.meses) : sumaDias(base, intervalo.dias || 0);
  const dias = Math.round((fecha.getTime() - (aDia(hoy) as Date).getTime()) / 86400000);
  const cuando = ddmmaaaa(fecha);
  if (dias < 0) {
    const texto = "Vencido hace " + plural(-dias, "día", "días");
    return { estado: "VENCIDO", fecha, dias, texto, detalle: "Tocaba el " + cuando + " · " + minuscula(texto) };
  }
  if (dias <= DIAS_AVISO) {
    const texto = dias === 0 ? "Vence hoy" : "Vence en " + plural(dias, "día", "días");
    return { estado: "POR_VENCER", fecha, dias, texto, detalle: "Toca el " + cuando + " · " + minuscula(texto) };
  }
  return {
    estado: "AL_DIA",
    fecha,
    dias,
    texto: "Próximo: " + cuando,
    detalle: cuando + " (en " + plural(dias, "día", "días") + ")",
  };
}

/**
 * Solo la fecha, en hora local. «2026-08-13» se toma como ese día aquí y no
 * en UTC, que en México lo recorrería al día anterior.
 */
function aDia(f: Fecha): Date | null {
  if (f === null || f === undefined || f === "") {
    return null;
  }
  if (typeof f === "string") {
    const m = f.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      return new Date(+m[1], +m[2] - 1, +m[3]);
    }
  }
  const d = new Date(f);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Suma meses sin brincarse al siguiente: 31 de enero + 1 mes = fin de febrero. */
function sumaMeses(d: Date, meses: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth() + meses, 1);
  const ultimo = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(d.getDate(), ultimo));
  return r;
}

function sumaDias(d: Date, dias: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dias);
}

function plural(n: number, uno: string, varios: string): string {
  return n + " " + (n === 1 ? uno : varios);
}

function minuscula(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

function ddmmaaaa(d: Date): string {
  return ("0" + d.getDate()).slice(-2) + "/" + ("0" + (d.getMonth() + 1)).slice(-2) + "/" + d.getFullYear();
}

function sinAcentos(s?: string | null): string {
  return (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}
